import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from '@mui/material';
import {ExpandMore as ExpandMoreIcon, InsertDriveFile as InsertDriveFileIcon, Image as ImageIcon, PictureAsPdf as PictureAsPdfIcon, Visibility as VisibilityIcon} from '@mui/icons-material';
import LabIcon from '@mui/icons-material/Biotech';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import AppStyles from '../styles/AppStyles';
import { processFilesWithCurrentToken } from '../utils/fileUtils';
import FileViewer from '../components/FileViewer';
import { CardSkeleton } from '../components/SkeletonLoader';
import SearchField from '../components/SearchField';
import ErrorState from '../components/ErrorState';
import NoEntries from '../components/NoEntries';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function LabResults() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [petId, setPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const [labResults, setLabResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // File viewer state
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [currentFiles, setCurrentFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  // Title is centrally rendered in Layout; no per-page header here

  const queryClient = useQueryClient();
  const token = typeof window !== 'undefined' ? localStorage.getItem('vetToken') : null;
  const currentPetId = typeof window !== 'undefined' ? (localStorage.getItem('currentPetId') || petId) : petId;
  useEffect(() => setPetId(currentPetId), [currentPetId]);

  const sectionsQuery = useQuery({
    queryKey: ['sections', currentPetId],
    enabled: !!currentPetId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sections?petId=${encodeURIComponent(currentPetId)}&added=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 120000,
  });

  const labSectionId = (sectionsQuery.data || []).find(s => {
    const name = String(s.name || '').trim().toLowerCase();
    return name === 'laboratory' || name === 'lab results' || name === 'labresults' || name.includes('lab');
  })?.id;

  const labResultsQuery = useQuery({
    queryKey: ['labResults', currentPetId, labSectionId],
    enabled: !!currentPetId && !!labSectionId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sections-lab-results?petId=${encodeURIComponent(currentPetId)}&sectionId=${encodeURIComponent(labSectionId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60000,
  });

  useEffect(() => {
    setLoading(sectionsQuery.isLoading || labResultsQuery.isLoading);
    setError(sectionsQuery.isError || labResultsQuery.isError ? t('labResults.failedToLoad') : null);
    if (labResultsQuery.data) setLabResults(labResultsQuery.data);
  }, [sectionsQuery.isLoading, labResultsQuery.isLoading, sectionsQuery.isError, labResultsQuery.isError, labResultsQuery.data, t]);

  // Listen for pet changes from Layout component and prefetch
  useEffect(() => {
    const handlePetChange = () => {
      const currentPetId = localStorage.getItem('currentPetId');
      if (currentPetId && currentPetId !== petId) {
        setPetId(currentPetId);
        queryClient.invalidateQueries({ queryKey: ['sections', currentPetId] });
      }
    };

    window.addEventListener('petChanged', handlePetChange);
    
    return () => {
      window.removeEventListener('petChanged', handlePetChange);
    };
  }, [petId, queryClient]);

  const filteredResults = labResults.filter(result => {
    const term = (searchTerm || '').toLowerCase();
    if (!term) return true;
    const matchesRecord = result.name?.toLowerCase().includes(term) ||
      result.clinicName?.toLowerCase().includes(term);
    const files = Array.isArray(result.files) ? result.files : [];
    const matchesAnyFile = files.some(f => {
      const name = (f?.fileName || '').toLowerCase();
      const url = (f?.url || '').toLowerCase();
      return name.includes(term) || url.includes(term);
    });
    return matchesRecord || matchesAnyFile;
  });

  // File viewer handlers
  const handleFileView = (files, fileIndex = 0) => {
    console.log('LabResults: Opening file viewer with files:', files);
    console.log('LabResults: Selected file index:', fileIndex);
    setCurrentFiles(files);
    setCurrentFileIndex(fileIndex);
    setFileViewerOpen(true);
  };

  const handleFileViewerClose = () => {
    setFileViewerOpen(false);
    setCurrentFiles([]);
    setCurrentFileIndex(0);
  };

  const handleFileIndexChange = (newIndex) => {
    setCurrentFileIndex(newIndex);
  };

  // Removed unused status utils from old mock UI

  const inferTypeFromExtension = (file) => {
    const value = (file?.fileName || file?.url || '').toLowerCase();
    try {
      const u = new URL(value);
      if (u.pathname) {
        const p = u.pathname.toLowerCase();
        if (p.endsWith('.jpg') || p.endsWith('.jpeg')) return 'image/jpeg';
        if (p.endsWith('.png')) return 'image/png';
        if (p.endsWith('.gif')) return 'image/gif';
        if (p.endsWith('.webp')) return 'image/webp';
        if (p.endsWith('.pdf')) return 'application/pdf';
      }
    } catch (_) {}
    if (value.endsWith('.jpg') || value.endsWith('.jpeg')) return 'image/jpeg';
    if (value.endsWith('.png')) return 'image/png';
    if (value.endsWith('.gif')) return 'image/gif';
    if (value.endsWith('.webp')) return 'image/webp';
    if (value.endsWith('.pdf')) return 'application/pdf';
    return file?.fileType || null;
  };

  // Render order: 1) loading 2) error 3) NoEntries or content
  if (loading) {
    return <CardSkeleton count={3} />;
  }
  if (error) {
    return <ErrorState />;
  }
  if ((labResults || []).length === 0) {
    return (
      <NoEntries
        title={t('common.noLabResultsTitle')}
        description={t('common.noLabResultsDesc')}
        icon="lab"
      />
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <SearchField 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
        placeholder={t('common.searchPlaceholder')}
        ariaLabel={t('labResults.searchPlaceholder')}
      />

      {/* Lab Results */}
      {filteredResults.map((result) => {
        const files = processFilesWithCurrentToken(Array.isArray(result.files) ? result.files : []);
        console.log('LabResults: Processed files for result', result.name, ':', files);
        const getFilePalette = (file) => {
          const t = (inferTypeFromExtension(file) || '').toLowerCase();
          if (t === 'application/pdf' || t.includes('pdf')) {
            return { bg: `linear-gradient(135deg, ${AppStyles.colors.error.main} 0%, ${AppStyles.colors.error.light} 100%)`, icon: AppStyles.colors.error.contrastText };
          }
          if (t === 'image' || t.startsWith('image/')) {
            return { bg: `linear-gradient(135deg, ${AppStyles.colors.info.main} 0%, ${AppStyles.colors.info.light} 100%)`, icon: AppStyles.colors.info.contrastText };
          }
          return { bg: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`, icon: AppStyles.colors.primary.contrastText };
        };
        const getFileIcon = (file) => {
          const t = (inferTypeFromExtension(file) || '').toLowerCase();
          if (t === 'application/pdf' || t.includes('pdf')) return <PictureAsPdfIcon sx={{ color: '#fff' }} />;
          if (t === 'image' || t.startsWith('image/')) return <ImageIcon sx={{ color: '#fff' }} />;
          return <InsertDriveFileIcon sx={{ color: '#fff' }} />;
        };
        return (
          <Accordion key={result.id} disableGutters sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
            <AccordionSummary 
              expandIcon={
                <>
                  <Box
                    sx={{
                      px: 1.6,
                      py: 0.6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 68,
                      borderRadius: 999,
                      color: '#fff',
                      fontWeight: 800,
                      letterSpacing: .2,
                      fontSize: 12,
                      lineHeight: 1,
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 60%, ${AppStyles.colors.secondary.main})`,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
                    }}
                    className="count-pill"
                  >
                    {files.length} {files.length === 1 ? t('labResults.file') : t('labResults.files')}
                  </Box>
                  <ExpandMoreIcon className="expand-icon" />
                </>
              }
              sx={{ 
                px: 2.5, 
                py: 1.5, 
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 }, 
                '& .MuiAccordionSummary-expandIconWrapper': { 
                  ml: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transform: 'none !important'
                },
                '& .MuiAccordionSummary-expandIconWrapper .expand-icon': {
                  transition: 'transform 200ms'
                },
                '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded .expand-icon': {
                  transform: 'rotate(180deg)'
                }
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
                }}
              >
                <LabIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Stack direction="column" spacing={0.8} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                  <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, color: AppStyles.colors.cardTitle }}>{result.name}</Typography>
                  <Box sx={{ flex: 1 }} />
                </Stack>
                {result.clinicName && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary"><strong>{t('labResults.clinic')}</strong> {result.clinicName}</Typography>
                  </Stack>
                )}
                {result.date && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary"><strong>{t('labResults.sampleDate')}</strong> {moment(result.date).format('DD MMM YYYY')}</Typography>
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2.5, pb: 2 }}>
              {files.length > 0 && (
                <List dense sx={{ mt: 1, px: 0 }}>
                  {files.map((f, idx) => (
                    <ListItem 
                      key={idx} 
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          onClick={() => handleFileView(files, idx)}
                          sx={{ 
                            color: AppStyles.colors.text.secondary,
                            '&:hover': { color: AppStyles.colors.text.primary }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      } 
                      sx={{ 
                        border: `1px solid ${AppStyles.colors.border.light}`, 
                        borderRadius: 2, 
                        mb: 1,
                        px: 1.25,
                        py: 1,
                        cursor: 'pointer',
                        transition: 'transform .12s ease, box-shadow .2s ease, background-color .2s ease',
                        backgroundColor: AppStyles.colors.background.paper,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        '&:hover': {
                          backgroundColor: AppStyles.colors.background.default,
                          boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => handleFileView(files, idx)}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            width: 36,
                            height: 36,
                            bgcolor: 'transparent',
                            color: '#fff',
                            border: `1px solid ${AppStyles.colors.border.light}`,
                            background: getFilePalette(f).bg,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.10)'
                          }}
                        >
                          {getFileIcon(f)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600, color: AppStyles.colors.text.primary }}>
                            {f.fileName || t('labResults.fileFallback')}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {result.notes && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('labResults.notes')}</strong> <Typography component="span" variant="body2" color="text.secondary">{result.notes}</Typography></Typography>
                  </Stack>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('labResults.created')} {moment(result.createdAt).format('MMM DD, YYYY')}</Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {filteredResults.length === 0 && labResults.length > 0 && (
        <Paper sx={{ p: { xs: 2, md: 4 }, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
{t('labResults.noResultsFound')}
          </Typography>
        </Paper>
      )}

      {/* File Viewer */}
      <FileViewer
        open={fileViewerOpen}
        onClose={handleFileViewerClose}
        files={currentFiles}
        currentIndex={currentFileIndex}
        onIndexChange={handleFileIndexChange}
      />
    </Box>
  );
}

export default LabResults;
